import { useEffect, useRef, useState } from 'react';

import { api } from '../../services/api';
import type { WhatsAppTemplate, TemplateCategory, TemplateComponent } from '../../types';

const categoryLabels: Record<TemplateCategory, string> = {
  marketing: 'Marketing',
  utility: 'Nuttig',
  authentication: 'Authenticatie',
};

const statusLabels: Record<string, string> = {
  draft: 'Concept',
  pending: 'In afwachting',
  approved: 'Goedgekeurd',
  rejected: 'Afgewezen',
  paused: 'Gepauzeerd',
};

const statusColors: Record<string, string> = {
  draft: 'draft',
  pending: 'pending',
  approved: 'approved',
  rejected: 'rejected',
  paused: 'paused',
};

const TemplateManager = () => {
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<WhatsAppTemplate | null>(null);
  const [name, setName] = useState('');
  const [category, setCategory] = useState<TemplateCategory>('marketing');
  const [language, setLanguage] = useState('nl');
  const [bodyText, setBodyText] = useState('');
  const [headerText, setHeaderText] = useState('');
  const [footerText, setFooterText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadedRef = useRef(false);

  const load = async () => {
    try {
      const data = await api.templates();
      setTemplates(data);
    } catch {
      setError('Templates konden niet worden geladen.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    void load();
  }, []);

  const resetForm = () => {
    setName('');
    setCategory('marketing');
    setLanguage('nl');
    setBodyText('');
    setHeaderText('');
    setFooterText('');
    setEditing(null);
    setShowForm(false);
    setError(null);
  };

  const handleEdit = (template: WhatsAppTemplate) => {
    setEditing(template);
    setName(template.name);
    setCategory(template.category);
    setLanguage(template.language);
    const body = template.components.find((c) => c.type === 'body');
    const header = template.components.find((c) => c.type === 'header');
    const footer = template.components.find((c) => c.type === 'footer');
    setBodyText(body?.text ?? '');
    setHeaderText(header?.text ?? '');
    setFooterText(footer?.text ?? '');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!name.trim() || !bodyText.trim()) return;
    setSaving(true);
    setError(null);
    const components: TemplateComponent[] = [];
    if (headerText.trim()) components.push({ type: 'header', format: 'text', text: headerText.trim() });
    components.push({ type: 'body', text: bodyText.trim() });
    if (footerText.trim()) components.push({ type: 'footer', text: footerText.trim() });
    try {
      if (editing) {
        const updated = await api.updateTemplate(editing.id, {
          name: name.trim(),
          category,
          language,
          components,
        });
        setTemplates((current) => current.map((t) => (t.id === updated.id ? updated : t)));
      } else {
        const created = await api.createTemplate({ name: name.trim(), category, language, components });
        setTemplates((current) => [created, ...current]);
      }
      resetForm();
    } catch {
      setError('Opslaan mislukt.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Weet je zeker dat je deze template wilt verwijderen?')) return;
    try {
      await api.deleteTemplate(id);
      setTemplates((current) => current.filter((t) => t.id !== id));
    } catch {
      setError('Verwijderen mislukt.');
    }
  };

  return (
    <div className="template-manager">
      <header className="panel-header">
        <div>
          <p className="eyebrow">WhatsApp</p>
          <h2>Templates (HSM)</h2>
        </div>
        <button
          type="button"
          className="button primary small"
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
        >
          + Nieuw
        </button>
      </header>

      <p className="tool-description">
        WhatsApp templates (HSM) zijn vooraf goedgekeurde berichten die buiten het 24-uur venster
        kunnen worden verstuurd. Templates moeten door Meta worden goedgekeurd.
      </p>

      {error && <p className="notice error small">{error}</p>}

      {showForm && (
        <div className="template-form">
          <h3>{editing ? 'Bewerken' : 'Nieuwe template'}</h3>
          <label className="tool-field">
            <span>Naam</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="bijv. appointment_reminder"
              maxLength={512}
            />
          </label>
          <div className="form-row">
            <label className="tool-field">
              <span>Categorie</span>
              <select value={category} onChange={(e) => setCategory(e.target.value as TemplateCategory)}>
                <option value="marketing">Marketing</option>
                <option value="utility">Nuttig</option>
                <option value="authentication">Authenticatie</option>
              </select>
            </label>
            <label className="tool-field">
              <span>Taal</span>
              <select value={language} onChange={(e) => setLanguage(e.target.value)}>
                <option value="nl">Nederlands</option>
                <option value="en">English</option>
                <option value="es">Español</option>
              </select>
            </label>
          </div>
          <label className="tool-field">
            <span>Header (optioneel)</span>
            <input
              type="text"
              value={headerText}
              onChange={(e) => setHeaderText(e.target.value)}
              placeholder="Bijv. Afspraak bevestigd"
              maxLength={60}
            />
          </label>
          <label className="tool-field">
            <span>Bericht tekst</span>
            <textarea
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
              placeholder="Beste {{1}}, uw afspraak staat op {{2}}. Gebruik {{1}}, {{2}} etc. voor variabelen."
              rows={4}
              maxLength={4096}
            />
          </label>
          <label className="tool-field">
            <span>Footer (optioneel)</span>
            <input
              type="text"
              value={footerText}
              onChange={(e) => setFooterText(e.target.value)}
              placeholder="Bijv. Met vriendelijke groet, Team"
              maxLength={60}
            />
          </label>
          <div className="button-row">
            <button
              type="button"
              className="button primary small"
              disabled={saving || !name.trim() || !bodyText.trim()}
              onClick={() => void handleSave()}
            >
              {saving ? 'Opslaan…' : editing ? 'Bijwerken' : 'Aanmaken'}
            </button>
            <button type="button" className="button secondary small" onClick={resetForm}>
              Annuleren
            </button>
          </div>
        </div>
      )}

      {loading && <p className="muted small">Laden…</p>}

      {!loading && !showForm && templates.length === 0 && (
        <p className="empty-state small">Nog geen templates. Maak er een aan.</p>
      )}

      <div className="template-list">
        {templates.map((template) => (
          <div key={template.id} className="template-item">
            <div className="template-item-header">
              <strong>{template.name}</strong>
              <span className={`template-status ${statusColors[template.status] ?? ''}`}>
                {statusLabels[template.status] ?? template.status}
              </span>
            </div>
            <div className="template-item-meta">
              <span className="template-category">{categoryLabels[template.category]}</span>
              <span className="muted">·</span>
              <span>{template.language}</span>
            </div>
            {template.components.find((c) => c.type === 'body')?.text && (
              <p className="template-preview">
                {template.components.find((c) => c.type === 'body')?.text?.slice(0, 120)}
                {(template.components.find((c) => c.type === 'body')?.text?.length ?? 0) > 120 ? '…' : ''}
              </p>
            )}
            <div className="template-item-actions">
              <button type="button" className="button secondary small" onClick={() => handleEdit(template)}>
                ✎
              </button>
              <button type="button" className="button danger small" onClick={() => void handleDelete(template.id)}>
                ×
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TemplateManager;
